import {
  useRef,
  useCallback,
  useEffect,
} from 'react';
import isNil from 'lodash/isNil';

const DEFAULT_RETRY_INTERVALS = [
  3 * 60 * 1000,
  9 * 60 * 1000,
];

export type FailSafeRetryConfig = {
  retryIntervals: number[];
};

type UseFailSafeRetryParams<TJobId = string | number> = {
  jobId: TJobId | null;
  isTerminal: boolean;
  isCancelled: boolean;
  onRetry: () => void;
  label?: string;
  config?: Partial<FailSafeRetryConfig>;
};

type TimerRef = ReturnType<typeof setTimeout> | null;

export default function useFailSafeRetry<TJobId = string | number>({
  jobId,
  isTerminal,
  isCancelled,
  onRetry,
  label,
  config: configOverride,
}: UseFailSafeRetryParams<TJobId>) {
  const retryIntervals = configOverride?.retryIntervals ?? DEFAULT_RETRY_INTERVALS;

  const retriesFiredRef = useRef<number>(0);
  const timerRefs = useRef<TimerRef[]>([]);

  const jobIdRef = useRef<TJobId | null>(jobId);
  const isTerminalRef = useRef<boolean>(isTerminal);
  const isCancelledRef = useRef<boolean>(isCancelled);
  const onRetryRef = useRef<() => void>(onRetry);

  useEffect(() => {
    jobIdRef.current = jobId;
    isTerminalRef.current = isTerminal;
    isCancelledRef.current = isCancelled;
    onRetryRef.current = onRetry;
  }, [
    jobId,
    isTerminal,
    isCancelled,
    onRetry,
  ]);

  const clearTimers = useCallback(() => {
    timerRefs.current.forEach((timer) => {
      if (!isNil(timer)) {
        clearTimeout(timer);
      }
    });
    timerRefs.current = [];
  }, []);

  const attemptRetry = useCallback((retryIndex: number) => {
    const retryNum = retryIndex + 1;
    const shouldCancelAttempt = retriesFiredRef.current >= retryNum
      || isNil(jobIdRef.current)
      || isTerminalRef.current
      || isCancelledRef.current;

    if (!shouldCancelAttempt) {
      retriesFiredRef.current = retryNum;
      console.info(
        `[${label ?? 'Unknown'}] [FailSafeRetry] Retry #${retryNum} fired for job`,
        jobIdRef.current,
      );
      onRetryRef.current();
    }
  }, [label]);

  const start = useCallback(() => {
    if (!isNil(jobId)) {
      clearTimers();
      retriesFiredRef.current = 0;

      timerRefs.current = retryIntervals.map((intervalMs, index) => (
        setTimeout(() => {
          timerRefs.current[index] = null;
          attemptRetry(index);
        }, intervalMs)
      ));
    }
  }, [jobId, retryIntervals, clearTimers, attemptRetry]);

  const stop = useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    if (isNil(jobId) || isTerminal || isCancelled) {
      clearTimers();
    }
  }, [jobId, isTerminal, isCancelled, clearTimers]);

  useEffect(() => () => {
    clearTimers();
  }, [clearTimers]);

  return {
    start,
    stop,
  };
}
